import {Component, effect, inject, OnInit, signal, WritableSignal} from '@angular/core';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {CompanyService} from '@services/company.service';
import {ICompanyDashboard} from '@models/company.interface';
import {toSignal} from '@angular/core/rxjs-interop';
import {CompanyDataService} from '@services/company-data.service';
import {ICompteAdministration, IPasswordUpdateRequest} from '@models/compteAdministration.interface';
import {ExecutiveSubscriptionState} from '@services/executive-subscription.state';
import {Eye, EyeOff, LucideAngularModule, Save, X} from '@shared/components/icons/google-icons';
import {NotificationService} from '@services/frontend/notification.service';
import {WalletService} from '@services/wallet.service';

@Component({
  selector: 'app-company-dashboard',
  imports: [RouterLink, LucideAngularModule],
  templateUrl: './company-dashboard.html',
})
export class CompanyDashboard implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly companyService = inject(CompanyService);
  private readonly companyData = inject(CompanyDataService);
  private readonly notificationService = inject(NotificationService);
  protected readonly executiveSubscriptionState = inject(ExecutiveSubscriptionState);
  private readonly walletService = inject(WalletService);

  dashboard: WritableSignal<ICompanyDashboard | null> = signal(null);
  compte: WritableSignal<ICompteAdministration | null> = signal(null);
  loading: WritableSignal<boolean> = signal(true);
  error: WritableSignal<boolean> = signal(false);
  mobilePlatform: WritableSignal<'ios' | 'android' | null> = signal(null);
  siret = '';
  params = toSignal(this.route.parent!.paramMap);

  passwordForm: WritableSignal<IPasswordUpdateRequest> = signal({
    nomUtilisateurUrssaf: null,
    passwordUrssaf: null,
    typeCompteUrssaf: null,
    nomUtilisateurImpotsGouv: null,
    passwordImpotsGouv: null,
    nomUtilisateurProvigis: null,
    passwordProvigis: null,
    nomUtilisateurIdenum: null,
    passwordIdenum: null,
    passwordProbtp: null,
    nomUtilisateurCibtp: null,
    passwordCibtp: null,
  });
  showPasswordImpots = signal(false);
  savingImpotsCredentials = signal(false);

  protected readonly Eye = Eye;
  protected readonly EyeOff = EyeOff;
  protected readonly Save = Save;
  protected readonly X = X;

  constructor() {
    this.mobilePlatform.set(this.detectMobilePlatform());
    effect(() => {
      const paramSiret = this.params()?.get('siret')!;
      if (paramSiret) {
        this.siret = paramSiret;
        this.load();
      }
    });
  }

  ngOnInit(): void {
    this.executiveSubscriptionState.ensureLoaded();
  }

  private detectMobilePlatform(): 'ios' | 'android' | null {
    const ua = navigator.userAgent || '';
    if (!/Mobi|Android|iPhone|iPad|iPod/i.test(ua)) {
      return null;
    }
    if (/iPad|iPhone|iPod/i.test(ua)) {
      return 'ios';
    }
    if (/Android/i.test(ua)) {
      return 'android';
    }
    return null;
  }

  downloadApplePass(): void {
    if (this.mobilePlatform() !== 'ios') {
      this.notificationService.error('Apple Wallet disponible sur IPhone ou IPad.', 4000);
      return;
    }
    this.walletService.getAppleWalletPassDownloadUrl(this.siret).subscribe({
      next: url => {
        window.location.href = url;
      },
      error: () => this.notificationService.error('Apple Wallet indisponible.', 4000),
    });
  }

  load(): void {
    this.loading.set(true);
    this.error.set(false);
    this.companyService.getDashboard(this.siret).subscribe({
      next: (d) => {
        this.dashboard.set(d);
        this.companyData.load(this.siret).subscribe({
          next: (compte) => {
            this.compte.set(compte);
            this.passwordForm.set({
              nomUtilisateurUrssaf: compte.nomUtilisateurUrssaf,
              passwordUrssaf: null,
              typeCompteUrssaf: compte.typeCompteUrssaf,
              nomUtilisateurImpotsGouv: compte.nomUtilisateurImpotsGouv,
              passwordImpotsGouv: null,
              nomUtilisateurProvigis: compte.nomUtilisateurProvigis,
              passwordProvigis: null,
              nomUtilisateurIdenum: compte.nomUtilisateurIdenum,
              passwordIdenum: null,
              passwordProbtp: null,
              nomUtilisateurCibtp: compte.nomUtilisateurCibtp,
              passwordCibtp: null,
            });
            this.loading.set(false);
          },
          error: () => {
            this.compte.set(null);
            this.loading.set(false);
          }
        });
      },
      error: () => {
        this.error.set(true);
        this.loading.set(false);
      },
    });
  }

  updateImpotsField(field: 'nomUtilisateurImpotsGouv' | 'passwordImpotsGouv', value: string): void {
    this.passwordForm.update(form => ({...form, [field]: value || null}));
  }

  hasImpotsCredentials(): boolean {
    return this.compte()?.mdpSecretImpotsGouv != null;
  }

  saveImpotsCredentials(): void {
    this.savingImpotsCredentials.set(true);
    this.companyService.updatePasswords(this.siret, this.passwordForm()).subscribe({
      next: () => {
        this.companyData.refresh(this.siret).subscribe({
          next: (compte) => {
            this.compte.set(compte);
            this.passwordForm.update(form => ({
              ...form,
              nomUtilisateurImpotsGouv: compte.nomUtilisateurImpotsGouv,
              passwordImpotsGouv: null,
            }));
            this.savingImpotsCredentials.set(false);
            this.notificationService.success('Identifiants Impots.gouv mis à jour', 3000);
            this.load();
          },
          error: () => this.savingImpotsCredentials.set(false),
        });
      },
      error: () => {
        this.savingImpotsCredentials.set(false);
        this.notificationService.error('Erreur lors de la mise à jour des identifiants', 3000);
      },
    });
  }

  deleteImpotsCredentials(): void {
    if (!confirm('Supprimer les identifiants Impots.gouv ?')) {
      return;
    }
    this.companyService.deleteCredentials(this.siret, 'impots').subscribe({
      next: () => {
        this.companyData.refresh(this.siret).subscribe((compte) => {
          this.compte.set(compte);
          this.passwordForm.update(form => ({
            ...form,
            nomUtilisateurImpotsGouv: compte.nomUtilisateurImpotsGouv,
            passwordImpotsGouv: null,
          }));
          this.notificationService.success('Identifiants Impots.gouv supprimés', 3000);
          this.load();
        });
      },
      error: () => this.notificationService.error('Erreur lors de la suppression des identifiants', 3000),
    });
  }
}
